import type { QueryParams } from '@/lib/types/common.types';
import type { Notification, NotificationType } from '@/lib/types/notification.types';
import apiClient from './client';
import { extractArrayResponse, extractResponseData, handleApiError } from './responseHelpers';

export const notificationsApi = {
  // Get all notifications
  getAll: async (params?: QueryParams): Promise<Notification[]> => {
    try {
      const response = await apiClient.get<{ data: Notification[]; pagination?: any }>(
        '/v1/notifications',
        { params }
      );
      return extractArrayResponse<Notification>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  // Get notification by ID
  getById: async (id: string): Promise<Notification> => {
    try {
      const response = await apiClient.get<{ data: Notification } | Notification>(
        `/v1/notifications/${id}`
      );
      return extractResponseData<Notification>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  // Mark as read
  markAsRead: async (id: string): Promise<Notification> => {
    try {
      const response = await apiClient.patch<
        { message?: string; data: Notification } | Notification
      >(`/v1/notifications/${id}/read`);
      return extractResponseData<Notification>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  // Mark all as read
  markAllAsRead: async (): Promise<void> => {
    await apiClient.patch('/v1/notifications/read-all');
  },

  // Delete notification
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/notifications/${id}`);
  },

  // Clear all notifications
  clearAll: async (): Promise<void> => {
    await apiClient.delete('/v1/notifications/clear-all');
  },

  // Get unread notifications
  getUnread: async (params?: QueryParams): Promise<Notification[]> => {
    try {
      const response = await apiClient.get<{ data: Notification[]; pagination?: any }>(
        '/v1/notifications/unread',
        { params }
      );
      return extractArrayResponse<Notification>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  // Get unread count
  getUnreadCount: async (): Promise<{ count: number }> => {
    try {
      const response = await apiClient.get<{ data: { count: number } } | { count: number }>(
        '/v1/notifications/unread/count'
      );
      return extractResponseData<{ count: number }>(response);
    } catch (error) {
      handleApiError(error);
    }
  },

  // Get notifications by type
  getByType: async (type: NotificationType, params?: QueryParams): Promise<Notification[]> => {
    try {
      const response = await apiClient.get<{ data: Notification[]; pagination?: any }>(
        `/v1/notifications/type/${type}`,
        { params }
      );
      return extractArrayResponse<Notification>(response);
    } catch (error) {
      handleApiError(error);
    }
  },
};
